<template>
  <!-- Full-viewport paywall shown once the free preview is spent. Rendered by
       every player surface (videoPage, webinar, live, livePage) so the block
       looks the same wherever the viewer hit it. -->
  <div id="preview_paywall" role="dialog" aria-modal="true" aria-label="נדרש מנוי">
    <div
      class="vp-paywall-bg"
      :style="bgImage ? 'background-image:url(' + bgImage + ')' : ''"
      aria-hidden="true"
    ></div>
    <div class="vp-paywall-overlay" aria-hidden="true"></div>

    <div class="vp-paywall-content" role="alert" aria-live="polite">
      <div class="vp-paywall-badge" aria-hidden="true">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
          <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
        </svg>
        <span>TOV+</span>
      </div>
      <h2 class="vp-paywall-headline">היי {{ displayName }},</h2>
      <p class="vp-paywall-sub">צפיתם בתצוגה המקדימה (2 דקות).<br>סיימו את ההרשמה כדי להמשיך לצפות.</p>
      <button type="button" class="vp-cta-btn" @click="onCta">סיימו את ההרשמה עכשיו</button>
    </div>

    <button type="button" class="vp-paywall-close" aria-label="סגור" @click="$router.go(-1)">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <line x1="18" y1="6" x2="6" y2="18"/>
        <line x1="6" y1="6" x2="18" y2="18"/>
      </svg>
    </button>
  </div>
</template>

<script>
export default {
  name: 'PreviewPaywall',

  props: {
    /** Viewer's display name, greeted at the top of the card. */
    displayName: { type: String, default: '' },
    /** Poster used as the blurred backdrop. */
    bgImage: { type: String, default: '' },
    /** Logged-in viewers go to billing; guests get the signup modal. */
    isLoggedIn: { type: Boolean, default: false },
  },

  methods: {
    onCta() {
      if (this.isLoggedIn) this.$router.push('/user?section=billing');
      else this.$store.commit('open_signup', { step: 1, plan_id: 0 });
    },
  },
};
</script>

<style scoped lang="scss">
#preview_paywall {
  position: fixed;
  inset: 0;
  z-index: 9999999;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.75);
}

.vp-paywall-bg {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  filter: blur(60px) brightness(0.18) saturate(1.4);
  transform: scale(1.2);
  z-index: 0;
}

.vp-paywall-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  z-index: 1;
}

.vp-paywall-content {
  position: relative;
  z-index: 2;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  width: calc(100% - 32px);
  max-width: 440px;
  padding: var(--space-8) var(--space-6);
  direction: rtl;
  background: rgba(8, 8, 8, 0.82);
  backdrop-filter: blur(28px) saturate(1.8);
  -webkit-backdrop-filter: blur(28px) saturate(1.8);
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: var(--radius-lg);
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.7), inset 0 0 0 1px rgba(255,255,255,0.04);
}

.vp-paywall-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: linear-gradient(135deg, var(--color-accent) 0%, var(--color-accentHover, #D4AF37) 100%);
  border-radius: var(--radius-sm);
  box-shadow: 0 4px 12px rgba(255, 215, 0, 0.25);

  svg {
    flex-shrink: 0;
    color: #000000;
  }

  span {
    font-family: var(--font-hebrew);
    font-size: 15px;
    font-weight: 800;
    letter-spacing: 0.5px;
    color: #000000;
    text-transform: uppercase;
  }
}

.vp-paywall-headline {
  font-family: var(--font-hebrew);
  font-size: 26px;
  font-weight: 700;
  color: var(--color-text);
  margin: 0;
  line-height: 1.3;
}

.vp-paywall-sub {
  font-family: var(--font-hebrew);
  font-size: 16px;
  line-height: 1.75;
  color: #B5B5B5;
  margin: 0;
}

.vp-cta-btn {
  width: 100%;
  height: 52px;
  font-size: 17px;
  font-family: var(--font-hebrew);
  font-weight: 700;
  padding-inline: 0;
  border: none;
  border-radius: var(--radius-md);
  background: var(--color-accent);
  color: #ffffff;
  cursor: pointer;
  transition: background 0.18s ease;

  &:hover { background: var(--color-accentHover); }
  &:active { background: var(--color-accentPress); }
}

.vp-paywall-close {
  position: absolute;
  top: calc(env(safe-area-inset-top, 0px) + var(--space-3));
  inset-inline-end: calc(env(safe-area-inset-right, 0px) + var(--space-3));
  z-index: 3;
  width: 44px;
  height: 44px;
  padding: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: var(--radius-sm);
  color: #ffffff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all var(--duration-fast) var(--easing-standard);

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.2);
  }

  &:focus-visible {
    outline: 2px solid var(--color-accent);
    outline-offset: 2px;
  }

  svg {
    width: 20px;
    height: 20px;
  }
}

@media (max-width: 480px) {
  .vp-paywall-content {
    padding: var(--space-6) var(--space-4);
    gap: var(--space-4);
  }
  .vp-paywall-headline { font-size: 22px; }
  .vp-paywall-sub { font-size: 15px; }
}
</style>
